import { useMutation } from '@tanstack/react-query';
import type { RegistryLookups, SubjectResponse, SubjectRoleResponse } from '@k-management/shared';
import { useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

/** «dal 01/10/2026 al 31/12/2027», or nothing. */
function validity(list: RegistryLookups['priceLists'][number]): string {
  if (!list.validFrom) return '';
  return list.validTo
    ? ` · dal ${formatDate(list.validFrom)} al ${formatDate(list.validTo)}`
    : ` · dal ${formatDate(list.validFrom)}`;
}

/**
 * The price lists of a client (proposal Listini §4): the quotes of the client
 * start from them. Empty is the Base. They change with «Assegnare i listini ai
 * clienti»; the list of costs is shown only with «Costi e margini».
 */
export function ClientPriceLists({
  subject,
  role,
  lookups,
  onChanged,
}: {
  subject: SubjectResponse;
  role: SubjectRoleResponse;
  lookups: RegistryLookups;
  onChanged: (updated?: SubjectResponse) => Promise<void>;
}) {
  const canAssign = useCan('anagrafica.price-lists');
  const seesCosts = 'costListCode' in role;
  const [editing, setEditing] = useState(false);
  const [activity, setActivity] = useState(role.activityListCode ?? '');
  const [cost, setCost] = useState(role.costListCode ?? '');
  const save = useMutation({
    mutationFn: () =>
      apiRequest<SubjectResponse>(`/subjects/${subject.id}/roles/${role.roleCode}`, {
        method: 'PATCH',
        body: {
          activityListCode: activity || null,
          ...(seesCosts ? { costListCode: cost || null } : {}),
        },
      }),
    onSuccess: async (updated) => {
      setEditing(false);
      await onChanged(updated);
    },
  });
  const lists = (kind: 'Attività' | 'Passività') =>
    lookups.priceLists.filter((list) => list.kind === kind);
  const name = (code: string | null | undefined, kind: 'Attività' | 'Passività') => {
    if (!code) return 'Base';
    const list = lookups.priceLists.find((item) => item.code === code && item.kind === kind);
    return list ? `${list.description}${validity(list)}` : `${code} (non più in tabella)`;
  };
  const choice = (
    kind: 'Attività' | 'Passività',
    value: string,
    onChange: (value: string) => void,
  ) => (
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Base</option>
      {lists(kind)
        .filter((list) => !list.code.startsWith('BASE-'))
        .map((list) => (
          <option key={list.code} value={list.code}>
            {list.description}
            {validity(list)}
          </option>
        ))}
    </select>
  );

  return (
    <section className="card stack">
      <h3>Listini</h3>
      {editing ? (
        <>
          <div className="form-grid">
            <Field label="Listino attività (prezzi di vendita)">
              {choice('Attività', activity, setActivity)}
            </Field>
            {seesCosts && (
              <Field label="Listino passività (costi)">{choice('Passività', cost, setCost)}</Field>
            )}
          </div>
          {save.error && <p className="error">{save.error.message}</p>}
          <div className="actions">
            <button type="button" onClick={() => setEditing(false)}>
              Annulla
            </button>
            <button
              type="button"
              className="primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              Salva
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="stack tight">
            <div>
              <small className="muted block">Listino attività</small>
              {name(role.activityListCode, 'Attività')}
            </div>
            {seesCosts && (
              <div>
                <small className="muted block">Listino passività</small>
                {name(role.costListCode, 'Passività')}
              </div>
            )}
          </div>
          <p className="muted small">
            I nuovi preventivi del cliente partono da questi listini; un listino scaduto lascia il
            posto al Base.
          </p>
          {canAssign && (
            <div>
              <button type="button" className="small" onClick={() => setEditing(true)}>
                Cambia i listini
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
